import { property, subclass } from '@arcgis/core/core/accessorSupport/decorators';
import { messageBundle, tsx } from '@arcgis/core/widgets/support/widget';
import Widget from '@arcgis/core/widgets/Widget';
import { VNode } from 'src/interfaces/interfaces';
import { EAppTypeIdentifier } from '../utils/Compatibility/webmapChecks';

import "./AppTypeInfoModal/styles/AppTypeInfoModal.scss";

export interface AppTypeInfoModalProperties extends __esri.WidgetProperties {
  picUrl: string;
  /** Ex: "Media", "Attachment Viewer", ...etc */
  appTypeTitle: string;
  appTypeUrlFragment: string;
  sendFocusOnClose: Function;
}

export const AppType_to_T9NKey = {
  [EAppTypeIdentifier.ThreeDViewer]: "3d",
  [EAppTypeIdentifier.AttachmentViewer]: "av",
  [EAppTypeIdentifier.CategoryGallery]: "catGal",
  [EAppTypeIdentifier.Countdown]: "countdown",
  [EAppTypeIdentifier.ImageryApp]: "imageView",
  [EAppTypeIdentifier.InteractiveLegend]: "intLeg",
  [EAppTypeIdentifier.Media]: "media",
  [EAppTypeIdentifier.Minimalist]: "minimalist",
  [EAppTypeIdentifier.Nearby]: "nearby",
  [EAppTypeIdentifier.Portfolio]: "portfolio",
  [EAppTypeIdentifier.ZoneLookup]: "zoneLook"
}

@subclass('Landing.widgets.AppTypeInfoModal')
export default class AppTypeInfoModal extends Widget {

  @property()
  picUrl: string;

  @property()
  appTypeTitle: string;

  @property()
  appTypeUrlFragment: string;

  @property()
  @messageBundle("dist/assets/t9n/App/App")
  appMessages;

  @property()
  sendFocusOnClose: Function;

  private _isActive: boolean = true;

  constructor(properties?: AppTypeInfoModalProperties) {
    super(properties);
  }

  render() {
    const { appDescriptions } = this.appMessages;

    const header = this._renderHeader();
    const descSection = this._renderDescription();
    const exSection = this._renderExamples();

    return (
      <calcite-modal
        class="app-description-modal"
        active={this._isActive}
        background-color="white"
        afterCreate={(modal: HTMLCalciteModalElement)=>{
          modal.addEventListener("calciteModalClose", ()=>{
            this._close();
          });
        }}
        scale="m"
        width="m"
        intl-close={appDescriptions.close}
      >

        { header }

        <div class="app-description-modal__description" slot="content">
          <img class="app-desc-image" src={this.picUrl} width="150px" alt="" />
          { descSection }
          { exSection }
        </div>

        <calcite-button
          slot="primary"
          width="auto"
          class="app-description-modal__close"
          onclick={()=>{ 
            this._close();
          }}
        >
          {appDescriptions.close}
        </calcite-button>
      </calcite-modal>
    );
  }

  private _close(){
    this._isActive = false;
    this.sendFocusOnClose?.();
    this.destroy();
  }

  private _renderHeader(): VNode {
    return (
      <h2 class="app-description-modal__header" slot="header">
        <div>
          {this.appTypeTitle}
        </div>
      </h2>
    );
  }

  private _renderDescription(): VNode[] {
    const { appDescriptions } = this.appMessages;
    const descKey = `${AppType_to_T9NKey[this.appTypeUrlFragment]}Desc`;

    // TODO: At 9.3 get ride of hard coded Countdown strings

    return [
      <h4>{appDescriptions.sectionTitle}</h4>,
      <p>{ this.appTypeUrlFragment === EAppTypeIdentifier.Countdown 
        ? "Display a ranked list of locations in a map. Summarize data based on ordered vales and add text to provide commentary. Allow viewers to pin features in the map to compare and contrast them."
        : appDescriptions[descKey]}</p>
    ];
  }

  private _tempHardcodeCountdownExs = [
    "Present the top 10 most visited national parks in a given year",
    "Provide commentary and reasoning behind ranked lists with additional text",
    "Compare and contrast features in a map to derive deeper understanding"
  ]

  private _renderExamples(): VNode[] {
    const { appExamples } = this.appMessages;
    const exKeyRoot = `${AppType_to_T9NKey[this.appTypeUrlFragment]}Ex`;

    // TODO: At 9.3 get ride of hard coded Countdown strings
    let renderList: any[];
    if(this.appTypeUrlFragment === EAppTypeIdentifier.Countdown){
      renderList = this._tempHardcodeCountdownExs.map((str)=>{
        return (
          <li>{str}</li>
        )
      })
    }else{
      renderList = Object.keys(appExamples)
      .filter(key => key.indexOf(exKeyRoot) !== -1)
      .map((exKey) => {
        return (
          <li>{appExamples[exKey]}</li>
        )
      })
    }

    return [
      <h4>{appExamples.sectionTitle}</h4>,
      <ul>
        { renderList }
      </ul>
    ];
  }

}
